<script setup>
import { computed } from 'vue'
import TbModal from '../foundations/TbModal.vue'
import { AlertTriangle } from 'lucide-vue-next'

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: 'Confirm Action'
  },
  message: {
    type: String,
    default: 'Are you sure you want to proceed?'
  },
  confirmText: {
    type: String,
    default: 'Confirm'
  },
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  variant: {
    type: String,
    default: 'danger',
    validator: (value) => ['danger', 'warning', 'info'].includes(value)
  },
  loading: {
    type: Boolean,
    default: false
  },
  icon: {
    type: Object,
    default: null
  },
  size: {
    type: String,
    default: 'sm',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  }
})

const emit = defineEmits(['update:show', 'confirm', 'cancel'])

const handleUpdateShow = (show) => {
  emit('update:show', show)
  if (!show) {
    emit('cancel')
  }
}

const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('update:show', false)
  emit('cancel')
}

const modalVariant = computed(() => {
  return props.variant === 'danger' ? 'destructive' : 'default'
})

const iconComponent = computed(() => {
  if (props.icon) return props.icon
  
  const defaultIcons = {
    danger: AlertTriangle,
    warning: AlertTriangle,
    info: AlertTriangle
  }
  
  return defaultIcons[props.variant]
})

const iconClasses = computed(() => {
  const classes = {
    danger: 'text-danger',
    warning: 'text-yellow-600',
    info: 'text-primary'
  }
  return classes[props.variant]
})

const confirmButtonClasses = computed(() => {
  const classes = {
    danger: 'bg-danger hover:bg-danger-hover focus:bg-danger-focus',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:bg-yellow-500',
    info: 'bg-primary hover:bg-primary-hover focus:bg-primary-focus'
  }
  return [
    'px-4 py-2 text-sm font-medium text-white rounded-base transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    classes[props.variant],
    props.variant === 'danger' ? 'focus:ring-danger-muted' : 
    props.variant === 'warning' ? 'focus:ring-yellow-300' : 'focus:ring-primary-muted'
  ].join(' ')
})

const warningText = computed(() => {
  const texts = {
    danger: 'This action cannot be undone.',
    warning: 'Please proceed with caution.',
    info: 'This will make changes to your data.'
  }
  return texts[props.variant]
})
</script>

<template>
  <TbModal
    :show="show"
    @update:show="handleUpdateShow"
    :size="size"
    :variant="modalVariant"
    :title="title"
    :persistent="loading"
    :allow-close="!loading"
  >
    <div class="space-y-4">
      <!-- Icon and message -->
      <div class="flex items-start space-x-3">
        <div 
          :class="[
            'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
            variant === 'danger' ? 'bg-danger-light' :
            variant === 'warning' ? 'bg-yellow-100' : 'bg-primary-light'
          ]"
        >
          <component
            :is="iconComponent"
            :size="20"
            :class="iconClasses"
          />
        </div>
        
        <div class="flex-1 min-w-0">
          <p class="text-gray-900 text-sm leading-5">
            {{ message }}
          </p>
          
          <p 
            v-if="variant === 'danger'"
            class="text-sm text-danger mt-2"
          >
            {{ warningText }}
          </p>
          
          <p 
            v-else-if="variant === 'warning'"
            class="text-sm text-yellow-700 mt-2"
          >
            {{ warningText }}
          </p>
          
          <p 
            v-else
            class="text-sm text-gray-600 mt-2"
          >
            {{ warningText }}
          </p>
        </div>
      </div>
    </div>

    <template #actions>
      <div class="flex justify-end gap-3 w-full">
        <button
          type="button"
          @click="handleCancel"
          :disabled="loading"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-base hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ cancelText }}
        </button>
        
        <button
          type="button"
          @click="handleConfirm"
          :disabled="loading"
          :class="confirmButtonClasses"
        >
          <span v-if="loading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>{{ confirmText }}</span>
        </button>
      </div>
    </template>
  </TbModal>
</template>